import { CascaderField, Form, SubmitButton } from '@/components';
import { useForm } from '@/hooks';
import { genRecursiveOptions } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  useExpenditureItemNatureOptions,
  useExpenditureNaturesComprehensive,
} from '../../hooks';
import { ExpenditureItemNaturePresetFormSchema } from '../../models';
import { expenditureNatureNameWithCode } from '../../utils';

type FormValues = z.infer<typeof ExpenditureItemNaturePresetFormSchema>;

type Props = {
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const ExpenditureItemNaturePresetForm: FC<Props> = ({
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(ExpenditureItemNaturePresetFormSchema),
  });

  const expenditureItemNatureOptions = useExpenditureItemNatureOptions({
    showCode: true,
  });

  const expenditureNatureOptionsComprehensive =
    useExpenditureNaturesComprehensive({
      select: (data) =>
        genRecursiveOptions(
          data.flatMap((item) => {
            if (!item.children) {
              return [];
            }
            return {
              ...item,
              name: expenditureNatureNameWithCode({
                code: item.code,
                name: item.name,
              }),
              children: item.children.map((v) => ({
                ...v,
                name: expenditureNatureNameWithCode({
                  code: v.code,
                  name: v.name,
                  level: v.level,
                }),
              })),
            };
          }),
          'id',
          'name',
        ),
    });

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <CascaderField
        name="expenditureNatureIds"
        label={t('expenditure-nature')}
        control={control}
        errors={errors}
        options={expenditureNatureOptionsComprehensive.data}
        required
      />

      <CascaderField
        name="expenditureItemNatureIds"
        label={t('expenditure-item-nature')}
        control={control}
        errors={errors}
        options={expenditureItemNatureOptions.data}
        required
      />

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
